import { SaveOutlined } from "@ant-design/icons";
import type { DrawerProps } from "../ui";
import { Button, Drawer, Flex, Grid, Space, theme } from "../ui";
import { ActiveToggle } from "../ui/ActiveToggle";
import { KitLockedContext } from "../ui/fieldContext";
import React from "react";
import { useAutoFocusInContainer } from "./hooks/useAutoFocusInContainer";
import { useSubmitLock } from "./hooks/useSubmitLock";
import { AsyncActionButton } from "./AsyncActionButton";
import { LoadingOverlay } from "./LoadingOverlay";
import { spacing } from "../settings/settingsListDesign";
import { useI18nOptional } from "../i18n/useI18n";
import { windowHeight } from "../theme/displayZoom";
import { drawerWidths } from "./drawerWidths";
import { useDrawerCloseGuard } from "./hooks/useDrawerCloseGuard";
import { useDrawerDraft } from "./hooks/useDrawerDraftScope";
import { drawerActions, drawerModes, type DrawerMode } from "./drawerModes";
import { useUiText } from "../ui/uiText";

/** Standard-Drawer-Breite (responsive), entspricht der m1member-Konvention. */
export const FORM_DRAWER_WIDTH = "min(720px, 96vw)";

export interface FormDrawerProps {
  /** Fachrolle; Ansichten und erhaltene Teilformulare besitzen keinen eigenen Abbruch. */
  mode?: DrawerMode;
  /** Ungespeicherter Entwurf, der beim Schließen verloren ginge (einschließlich Zustand außerhalb der Form). */
  dirty?: boolean | (() => boolean);
  contentLoading?: boolean;
  submitDisabled?: boolean;
  /** Gesture-driven primary controls (e.g. WebAuthn) occupy the same slot after cancel. */
  submitControl?: React.ReactNode;
  maskClosable?: boolean;
  loadingLabel?: string;
  /** Kompatibilitätswert; laufende Aktionen blockieren den Bildschirm nicht mehr. */
  loadingScreenTopInset?: string;
  /** Kompatibilitätswert; die Verarbeitung blendet den Drawer aus und erhält seinen Entwurf. */
  submissionOverlayScope?: "screen" | "block";
  /** Drawer-Titel. Bereits übersetzt übergeben (z. B. `tr("Edit campaign")`). */
  title: React.ReactNode;
  open: boolean;
  onCancel: () => void;

  /**
   * Wenn gesetzt: Submit-Button wird zu htmlType="submit" form={formId}. Enter im Form
   * triggert dann nativ den Submit. Die innere Form muss id={formId} + onFinish setzen.
   */
  formId?: string;
  /** Tatsächliche Verarbeitung: Drawer ausblenden, Eingaben bis zum Ergebnis erhalten. */
  submitting?: boolean;
  /**
   * Alternativ zu formId: klassischer onClick-Handler am Submit-Button. Wird nur
   * verwendet, wenn formId NICHT gesetzt ist. Vermeidet Enter-Submit — nur für reine
   * Anzeige-Drawer oder externe Connect-Buttons einsetzen.
   */
  onSave?: () => Promise<void> | void;

  // Button-Konfiguration — Texte bereits übersetzt übergeben.
  /** Default: "Save" (Aufrufer sollte `tr("Save")` übergeben). */
  submitText?: React.ReactNode;
  submitIcon?: React.ReactNode;
  /** Default: "Cancel" (Aufrufer sollte `tr("Cancel")` übergeben). */
  cancelText?: React.ReactNode;
  /** Roter Submit-Button (destruktive Aktion). */
  dangerous?: boolean;
  /** Submit-Button ausblenden (reine Anzeige oder externer Connect-Button). */
  hideSubmit?: boolean;
  /** Inhalt links neben Cancel/Submit (z. B. Löschen-Button). */
  extra?: React.ReactNode;
  /**
   * Nur ansehen (Ronny, 18.10.2026): Objekt ohne Bearbeitungsmöglichkeit (gebucht, automatisch, ohne Recht).
   * Alle Kit-Bedienelemente im Inhalt sind gesperrt, Speichern, `extra` und Aktivschalter entfallen,
   * Verlassen erfolgt über X, Escape oder den zulässigen Außenklick.
   */
  readOnly?: boolean;

  // Aktivzustand des Objekts im Kopf (opt-in), dargestellt als `ActiveToggle` („aktiv“/„inaktiv“)
  enabled?: boolean;
  onEnabledChange?: (enabled: boolean) => void;
  showEnabledSwitch?: boolean;
  /** Zugänglicher Name des Aktivzustands (z. B. „Konto aktiv“); sichtbar steht nur „aktiv“/„inaktiv“. */
  enabledSwitchLabel?: React.ReactNode | false;

  // UX
  /** Default true: erstes Eingabefeld bekommt nach Open Fokus. */
  autoFocusFirstField?: boolean;
  /** Default: responsive Standardbreite (`FORM_DRAWER_WIDTH`). */
  width?: number | string;
  /** Default "right". */
  placement?: DrawerProps["placement"];
  /** Drawer-Props, die nicht abgedeckt sind. Nur in Ausnahmefällen. */
  drawerProps?: Omit<
    DrawerProps,
    "open" | "title" | "onClose" | "width" | "placement" | "extra"
  >;

  children: React.ReactNode;
}

/**
 * Drawer-Wrapper mit standardisierter Tastatur-UX und einheitlichem Footer:
 *  - Buttons oben rechts (extra-Slot): optionale extra-Aktionen | Cancel | Submit.
 *  - Enter im Form-Body submittet (wenn formId gesetzt).
 *  - Escape schließt (Antd-Default).
 *  - AutoFocus auf erstes Eingabefeld nach Open.
 *
 * Produktneutraler Port aus m1member. i18n liegt beim Aufrufer: Titel und
 * Button-Texte werden als bereits übersetzte `ReactNode` übergeben.
 *
 * Aufrufer-Pflicht (Enter-Submit):
 *   <Form id={formId} onFinish={…}> … </Form>
 */
export function FormDrawer({
  mode = drawerModes.edit,
  dirty = false,
  contentLoading,
  submitDisabled = false,
  submitControl,
  maskClosable = true,
  loadingLabel,
  loadingScreenTopInset: _loadingScreenTopInset,
  submissionOverlayScope: _submissionOverlayScope = "screen",
  title,
  open,
  onCancel,
  formId,
  submitting,
  onSave,
  submitText,
  submitIcon,
  cancelText,
  dangerous = false,
  hideSubmit = false,
  extra,
  readOnly = false,
  enabled,
  onEnabledChange,
  showEnabledSwitch = false,
  enabledSwitchLabel = false,
  autoFocusFirstField = true,
  width = drawerWidths.standard,
  placement = "right",
  drawerProps,
  children,
}: FormDrawerProps) {
  const resolvedSubmitIcon = submitIcon ?? <SaveOutlined />;
  const { text: uiText } = useUiText();
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  // Ein Verhalten für alle Produkte (früher Variante „m1“): mobil von unten, während des Speicherns gesperrt.
  const native = true;
  const mobile = native && screens.md === false;
  const loading = Boolean(submitting);
  const actions = drawerActions(mode, readOnly, hideSubmit);
  useDrawerDraft(dirty, open, loading);
  const submitLocked = submitDisabled || contentLoading === true;
  const resolvedDrawerProps: DrawerProps = { ...drawerProps };
  if (native) { delete resolvedDrawerProps.width; delete resolvedDrawerProps.height; delete resolvedDrawerProps.size; }
  const canDismiss = !native || !loading;
  const handleCancel = useDrawerCloseGuard(onCancel, dirty, canDismiss, open);
  const drawerStyles = typeof drawerProps?.styles === "function" ? undefined : drawerProps?.styles;

  // Doppelklick und doppeltes Enter fangen: `submitting` greift erst nach dem naechsten
  // Render, zwei schnelle Ausloeser laufen sonst beide durch.
  const { submitBlocked, armSubmitLock } = useSubmitLock({
    open,
    loading: Boolean(submitting),
    formId,
    submitVisible: actions.submit,
    submitDisabled: submitLocked,
  });

  const submitButton = !actions.submit
    ? null
    : formId
      ? (
          <AsyncActionButton
            key="submit"
            type="primary"
            danger={dangerous}
            htmlType="submit"
            form={formId}
            icon={resolvedSubmitIcon}
            loading={native ? submitBlocked : submitting}
            backgroundPending={Boolean(submitting)}
            disabled={submitBlocked || submitLocked}
          >
            {submitText ?? uiText("Save")}
          </AsyncActionButton>
        )
      : (
          <AsyncActionButton
            key="submit"
            type="primary"
            danger={dangerous}
            icon={resolvedSubmitIcon}
            loading={native ? submitBlocked : submitting}
            backgroundPending={Boolean(submitting)}
            disabled={submitBlocked || submitLocked}
            onClick={() => {
              if (!armSubmitLock()) return;
              return onSave?.();
            }}
          >
            {submitText ?? uiText("Save")}
          </AsyncActionButton>
        );

  const containerRef = useAutoFocusInContainer({
    open,
    enabled: autoFocusFirstField && !readOnly && contentLoading !== true,
  });

  const hasActions = !readOnly && Boolean(actions.cancel || actions.submit || submitControl || extra
    || showEnabledSwitch && enabled !== undefined && onEnabledChange);
  const actionContent = !hasActions ? null : <>
    {showEnabledSwitch && enabled !== undefined && onEnabledChange ? <ActiveToggle checked={enabled} disabled={native && loading}
      onChange={(checked) => onEnabledChange(checked)} aria-label={typeof enabledSwitchLabel === "string" ? enabledSwitchLabel : undefined} /> : null}
    {extra}
    {actions.cancel && <Button disabled={native && loading} onClick={handleCancel}>{cancelText ?? uiText("Cancel")}</Button>}
    {submitControl ?? submitButton}
  </>;
  const body = contentLoading === undefined ? children :
    <LoadingOverlay visible={contentLoading === true} loadingLabel={loadingLabel}>{children}</LoadingOverlay>;

  return (
    <>
    <Drawer
      {...resolvedDrawerProps}
      mask={mode === drawerModes.preview ? false : resolvedDrawerProps.mask}
      title={title}
      open={open}
      submitting={loading}
      onClose={handleCancel}
      width={mobile ? undefined : width}
      height={mobile ? windowHeight("100dvh", `${spacing.sm}px`) : resolvedDrawerProps.height}
      placement={mobile ? "bottom" : placement}
      closable={canDismiss ? resolvedDrawerProps.closable ?? true : false}
      maskClosable={mode !== drawerModes.preview && canDismiss && (resolvedDrawerProps.maskClosable ?? maskClosable)}
      keyboard={canDismiss && (resolvedDrawerProps.keyboard ?? true)}
      destroyOnHidden={mode === drawerModes.retained ? false : resolvedDrawerProps.destroyOnHidden ?? resolvedDrawerProps.destroyOnClose ?? true}
      extra={mobile || actionContent === null ? undefined : <Space>{actionContent}</Space>}
      footer={mobile && actionContent !== null ? <Flex justify="flex-end" align="center" gap={spacing.sm} wrap="wrap" style={{ width: "100%" }}>{actionContent}</Flex> : drawerProps?.footer}
      styles={native ? { ...drawerStyles,
        body: { ...drawerStyles?.body, ...(mobile ? { padding: `${spacing.lg}px ${spacing.lg}px ${memberDrawerMobileBottomPadding}px`, overflowX: "hidden", overflowY: "auto" } : {}) },
        footer: { ...drawerStyles?.footer, ...(mobile ? { padding: `${spacing.sm}px ${spacing.md}px`, background: token.colorBgElevated, borderTop: `1px solid ${token.colorSplit}`, boxShadow: token.boxShadowSecondary } : {}) },
      } : drawerProps?.styles}
    >
      <div ref={containerRef}>{readOnly ? <KitLockedContext.Provider value={true}>{body}</KitLockedContext.Provider> : body}</div>
    </Drawer>
    </>
  );
}

// Existing member mobile content inset: app bottom padding (48) plus its large section space (32).
const memberDrawerMobileBottomPadding = 80;

/**
 * Native member behavior with the shared app's translated defaults and external step-up dialogs.
 * Apps without the shared `I18nProvider` (m1member) pass all three texts as props.
 */
export function MemberFormDrawer(props: FormDrawerProps) {
  const tr = useI18nOptional()?.tr;
  return <FormDrawer {...props} submissionOverlayScope={props.submissionOverlayScope ?? "block"}
    submitText={props.submitText ?? tr?.("FormDrawer.save")} cancelText={props.cancelText ?? tr?.("FormDrawer.cancel")}
    loadingLabel={props.loadingLabel ?? tr?.("FormDrawer.loading")} />;
}
